import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { topNavStyle } from "../components/nav/topNavStyle";
import { supabase } from "../supabase/supabase";
import BrandWordmark from "../components/BrandWordmark";

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid var(--color-border)',
  fontSize: 15,
  color: 'var(--color-text-strong)',
  outline: 'none',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-page)',
};

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  marginBottom: 6,
};

function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [error, setError] = useState("");

  const is18OrOlder = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    const age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    return age > 18 || (age === 18 && m >= 0);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    if (!is18OrOlder(dob)) { setError("You must be at least 18 years old to sign up."); return; }
    try {
      const { error: signUpError } = await supabase.auth.signUp({
        email, password,
        options: { data: { first_name: firstName, last_name: lastName, dob } },
      });
      if (signUpError) throw signUpError;
      navigate("/verify-email", { state: { email }, replace: true });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="theme-auto" style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-card)', display: 'flex', flexDirection: 'column' }}>

      {/* Nav */}
      <nav style={topNavStyle({ paddingX: 24, paddingY: 16 })}>
        <Link to="/"><BrandWordmark width="clamp(100px, 25vw, 160px)" /></Link>
        <Link to="/login" style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-cta)', textDecoration: 'none' }}>
          Log in
        </Link>
      </nav>

      {/* Form */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--color-text-strong)', marginBottom: 8 }}>Create your account</h1>
          <p style={{ fontSize: 15, color: 'var(--color-text-muted)', marginBottom: 32 }}>Free to play. No experience needed.</p>

          {error && (
            <div style={{ backgroundColor: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 24 }}>
              <p style={{ fontSize: 14, color: 'var(--color-danger-text)', margin: 0 }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>First name</label>
                <input type="text" name="given-name" autoComplete="given-name" placeholder="First" value={firstName} onChange={e => setFirstName(e.target.value)} required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Last name</label>
                <input type="text" name="family-name" autoComplete="family-name" placeholder="Last" value={lastName} onChange={e => setLastName(e.target.value)} required style={inputStyle} />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Date of birth</label>
              <input type="date" name="bday" autoComplete="bday" value={dob} onChange={e => setDob(e.target.value)} required style={inputStyle} />
              <p style={{ fontSize: 12, color: 'var(--color-text-faint)', marginTop: 4 }}>Must be 18 or older</p>
            </div>

            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Password</label>
              <input type="password" name="new-password" id="new-password" autoComplete="new-password" placeholder="Create a password" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
            </div>

            <div>
              <label style={labelStyle}>Confirm password</label>
              <input type="password" name="confirm-password" autoComplete="new-password" placeholder="Confirm your password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required style={inputStyle} />
            </div>

            <button
              type="submit"
              style={{
                padding: '14px 0', borderRadius: 50, border: 'none', cursor: 'pointer',
                backgroundColor: 'var(--color-cta)', color: 'var(--color-cta-text)', fontWeight: 700, fontSize: 16, marginTop: 8,
              }}
            >
              Create account
            </button>
          </form>

          <p style={{ fontSize: 12, color: 'var(--color-text-faint)', textAlign: 'center', marginTop: 20 }}>
            By signing up you agree to our{' '}
            <Link to="/terms" style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>terms of service</Link>{' '}
            and{' '}
            <Link to="/privacy" style={{ color: 'var(--color-text-muted)', fontWeight: 600 }}>privacy policy</Link>.
            Lineup is a skill-based fantasy game for users 18+.
          </p>

          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', textAlign: 'center', marginTop: 16 }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--color-cta)', fontWeight: 600, textDecoration: 'none' }}>Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;
